import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, FileText, Clock, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showErrorToast } from '../../utils/errorHandling';

const Analytics = () => {
  const [analytics, setAnalytics] = useState({
    overview: {
      totalQuestions: 0,
      totalKits: 0,
      activeUsers: 0,
      completionRate: 0
    },
    productivity: {
      questionsCreatedToday: 0,
      questionsReviewedToday: 0,
      averageReviewTime: 0,
      pendingReviews: 0
    },
    teamPerformance: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      // For now, we'll show placeholder data since the API might not be implemented yet
      setAnalytics({
        overview: {
          totalQuestions: 1247,
          totalKits: 23,
          activeUsers: 12,
          completionRate: 87.5
        },
        productivity: {
          questionsCreatedToday: 15,
          questionsReviewedToday: 8,
          averageReviewTime: 24,
          pendingReviews: 45
        },
        teamPerformance: [
          {
            name: 'Sarah Johnson',
            role: 'Content Generator',
            questionsCreated: 123,
            averageQuality: 4.2,
            efficiency: 92
          },
          {
            name: 'Mike Davis',
            role: 'Content Reviewer',
            questionsReviewed: 89,
            averageReviewTime: 18,
            accuracy: 96
          },
          {
            name: 'John Smith',
            role: 'Content Supervisor',
            assignmentsCreated: 15,
            teamEfficiency: 88,
            completionRate: 94
          }
        ],
        recentActivity: [
          {
            id: '1',
            type: 'Question Created',
            user: 'Sarah Johnson',
            description: 'Created 5 new mathematics questions',
            timestamp: '2024-01-16T14:30:00Z'
          },
          {
            id: '2',
            type: 'Review Completed',
            user: 'Mike Davis',
            description: 'Completed review of Physics KIT questions',
            timestamp: '2024-01-16T13:45:00Z'
          },
          {
            id: '3',
            type: 'KIT Published',
            user: 'John Smith',
            description: 'Published Mathematics Foundation KIT',
            timestamp: '2024-01-16T11:20:00Z'
          }
        ]
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      showErrorToast(error, 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'Question Created':
        return FileText;
      case 'Review Completed':
        return CheckCircle;
      case 'KIT Published':
        return TrendingUp;
      default:
        return AlertCircle;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'Question Created':
        return 'text-blue-600';
      case 'Review Completed':
        return 'text-green-600';
      case 'KIT Published':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  const periodOptions = [
    { value: '1d', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor content creation performance and team productivity</p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {periodOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CardLoadingSpinner />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{analytics.overview.totalQuestions}</div>
                  <div className="text-sm text-gray-600">Total Questions</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{analytics.overview.totalKits}</div>
                  <div className="text-sm text-gray-600">Total KITs</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{analytics.overview.activeUsers}</div>
                  <div className="text-sm text-gray-600">Active Users</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{analytics.overview.completionRate}%</div>
                  <div className="text-sm text-gray-600">Completion Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Productivity Stats */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Today's Productivity</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{analytics.productivity.questionsCreatedToday}</div>
                <div className="text-sm text-gray-600">Questions Created</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{analytics.productivity.questionsReviewedToday}</div>
                <div className="text-sm text-gray-600">Questions Reviewed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">{analytics.productivity.averageReviewTime}h</div>
                <div className="text-sm text-gray-600">Avg Review Time</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{analytics.productivity.pendingReviews}</div>
                <div className="text-sm text-gray-600">Pending Reviews</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Team Performance */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Performance</h3>
              <div className="space-y-4">
                {analytics.teamPerformance.map((member, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{member.name}</div>
                      <div className="text-xs text-gray-500">{member.role}</div>
                    </div>
                    <div className="text-right">
                      {member.questionsCreated && (
                        <div className="text-sm text-gray-900">{member.questionsCreated} created</div>
                      )}
                      {member.questionsReviewed && (
                        <div className="text-sm text-gray-900">{member.questionsReviewed} reviewed</div>
                      )}
                      {member.assignmentsCreated && (
                        <div className="text-sm text-gray-900">{member.assignmentsCreated} assignments</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                {analytics.recentActivity.map((activity) => {
                  const ActivityIcon = getActivityIcon(activity.type);
                  const iconColor = getActivityColor(activity.type);
                  return (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 ${iconColor}`}>
                        <ActivityIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{activity.type}</div>
                        <div className="text-sm text-gray-500">{activity.description}</div>
                        <div className="text-xs text-gray-400 flex items-center mt-1">
                          <Users className="h-3 w-3 mr-1" />
                          {activity.user} • {new Date(activity.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
